import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Eye, Trash2, Phone, Mail } from "lucide-react";
import { api } from "../../lib/api";
import { PageHeader, AdminBody, Card, Table, Modal, Button, StatusBadge, Toolbar } from "../../components/admin/Bits";

const STATUSES = ["new", "contacted", "follow_up", "converted", "lost"];

export default function Leads() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/leads").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const filtered = filter === "all" ? rows : rows.filter(r => r.status === filter || (!r.status && filter === "new"));

  const update = async (patch) => {
    await api.put(`/admin/leads/${editing.id}`, patch);
    toast.success("Updated"); load();
    setEditing({ ...editing, ...patch });
  };

  const remove = async (r) => {
    if (!window.confirm("Delete lead?")) return;
    await api.delete(`/admin/leads/${r.id}`); toast.success("Deleted"); load();
  };

  return (
    <>
      <PageHeader title="Leads & CRM" subtitle="All inquiries from the website land here." />
      <AdminBody>
        <Toolbar>
          <div className="flex gap-2 flex-wrap">
            {["all", ...STATUSES].map(s => (
              <button key={s} onClick={() => setFilter(s)} className={`text-[10px] tracking-[0.2em] uppercase px-3 py-2 border ${filter === s ? "bg-navy-900 text-gold border-navy-900" : "border-navy-900/15 hover:border-gold"}`} data-testid={`lead-filter-${s}`}>
                {s} {s !== "all" && <span className="ml-2 text-navy-900/60">{rows.filter(r => r.status === s).length}</span>}
              </button>
            ))}
          </div>
        </Toolbar>
        <Card>
          <Table
            columns={[
              { title: "Name", render: r => <><div className="serif text-lg">{r.name}</div><div className="text-navy-900/50 text-xs">{new Date(r.created_at).toLocaleString()}</div></> },
              { title: "Contact", render: r => <div className="text-xs space-y-1"><div className="flex items-center gap-1"><Phone size={11} className="text-gold" />{r.phone}</div>{r.email && <div className="flex items-center gap-1"><Mail size={11} className="text-gold" />{r.email}</div>}</div> },
              { title: "Event", render: r => <><div>{r.event_type || "—"}</div><div className="text-navy-900/50 text-xs">{r.event_date} · {r.guests ? `${r.guests} guests` : ""}</div></> },
              { title: "Package", render: r => <span className="text-gold">{r.package || "—"}</span> },
              { title: "Status", render: r => <StatusBadge status={r.status || "new"} /> },
            ]}
            rows={filtered}
            testIdPrefix="lead"
            actions={(r) => (
              <div className="flex gap-2 justify-end">
                <button onClick={() => { setEditing(r); setOpen(true); }} className="text-navy-900 hover:text-gold p-2" data-testid={`lead-view-${r.id}`}><Eye size={14} /></button>
                <button onClick={() => remove(r)} className="text-maroon hover:opacity-70 p-2"><Trash2 size={14} /></button>
              </div>
            )}
          />
        </Card>

        <Modal open={open} onClose={() => setOpen(false)} title="Lead Details" maxWidth="max-w-3xl">
          {editing && (
            <div className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <Info label="Name" value={editing.name} />
                <Info label="Phone" value={<a href={`tel:${editing.phone}`} className="text-gold">{editing.phone}</a>} />
                <Info label="Email" value={editing.email || "—"} />
                <Info label="Event" value={editing.event_type || "—"} />
                <Info label="Event Date" value={editing.event_date || "—"} />
                <Info label="Guests" value={editing.guests || "—"} />
                <Info label="Venue" value={editing.venue || "—"} />
                <Info label="Package Interest" value={editing.package || "—"} />
              </div>
              {editing.message && <div><div className="label-eyebrow mb-1">Message</div><div className="bg-ivory-200 p-4 text-sm">{editing.message}</div></div>}

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">Status</label>
                  <select className="input" value={editing.status || "new"} onChange={e => update({ status: e.target.value })} data-testid="lead-status">
                    {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Follow up date</label>
                  <input className="input" type="date" value={editing.follow_up_at || ""} onChange={e => update({ follow_up_at: e.target.value })} data-testid="lead-followup" />
                </div>
              </div>
              <div>
                <label className="label">Notes</label>
                <textarea className="input min-h-[80px]" defaultValue={editing.notes || ""} onBlur={e => update({ notes: e.target.value })} data-testid="lead-notes" />
              </div>
              <div className="flex gap-3">
                <Button onClick={() => window.location.href = `/admin/quotations?lead=${editing.id}`} data-testid="lead-convert">Create Quotation</Button>
                <a href={`https://wa.me/${(editing.phone || "").replace(/[^\d]/g, "")}`} target="_blank" rel="noreferrer" className="btn-outline">WhatsApp</a>
              </div>
            </div>
          )}
        </Modal>
      </AdminBody>
    </>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="label-eyebrow">{label}</div>
      <div className="mt-1">{value}</div>
    </div>
  );
}
